import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Eye, EyeOff, Loader2, Shield, Check } from 'lucide-react';
import { SENDAFooter } from '../components/SENDAFooter';

export default function CambiarPassword() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { changePassword, user } = useAuth();
    const navigate = useNavigate();

    const passwordRequirements = [
        { met: password.length >= 8, text: 'Al menos 8 caracteres' },
        { met: /[A-Z]/.test(password), text: 'Una letra mayúscula' },
        { met: /[0-9]/.test(password), text: 'Un número' },
        { met: password === confirmPassword && password.length > 0, text: 'Las contraseñas coinciden' }
    ];

    const allRequirementsMet = passwordRequirements.every(r => r.met);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!allRequirementsMet) {
            setError('Por favor cumple con todos los requisitos de la contraseña');
            return;
        }

        setLoading(true);
        const result = await changePassword(password);
        setLoading(false);

        if (result.success) {
            if (user?.role === 'mentor') {
                navigate('/mentor');
            } else {
                navigate('/usuario');
            }
        } else {
            setError(result.error);
        }
    };

    return (
        <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center p-4 relative overflow-hidden">
            {/* Background texture */}
            <div 
                className="absolute inset-0 opacity-30"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1708901083129-6922dff806cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjYWxtaW5nJTIwd2F0ZXIlMjByaXBwbGVzJTIwdGV4dHVyZXxlbnwwfHx8fDE3NzU1ODc2Mjh8MA&ixlib=rb-4.1.0&q=85)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            />
            
            <Card className="w-full max-w-md senda-card relative z-10 animate-fade-in-up">
                <CardHeader className="space-y-4 text-center pb-8">
                    <div className="mx-auto w-16 h-16 bg-[#7F9C8B] rounded-2xl flex items-center justify-center mb-2">
                        <Shield className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="senda-heading-2">Crear nueva contraseña</CardTitle>
                    <CardDescription className="senda-body">
                        Por tu seguridad, debes cambiar tu contraseña temporal antes de continuar.
                    </CardDescription>
                </CardHeader>
                
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div className="flex items-center gap-2 p-4 bg-[#D07F7E]/10 border border-[#D07F7E]/20 rounded-lg text-[#D07F7E]" data-testid="change-password-error">
                                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                                <span className="text-sm">{error}</span>
                            </div>
                        )}
                        
                        <div className="space-y-2">
                            <Label htmlFor="password" className="senda-label">Nueva contraseña</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="senda-input pr-10"
                                    required
                                    data-testid="new-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA5B1] hover:text-[#616E7C] transition-colors"
                                >
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="senda-label">Confirmar contraseña</Label>
                            <Input
                                id="confirmPassword"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="••••••••"
                                className="senda-input"
                                required
                                data-testid="confirm-password-input"
                            />
                        </div>
                        
                        {/* Password requirements */}
                        <div className="p-4 bg-[#FBFBF9] rounded-lg border border-[#EAE8E3]">
                            <p className="text-sm font-medium text-[#425A65] mb-3">Requisitos de la contraseña:</p>
                            <ul className="space-y-2">
                                {passwordRequirements.map((req) => (
                                    <li key={req.text} className="flex items-center gap-2">
                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${req.met ? 'bg-[#7F9C8B]' : 'bg-[#EAE8E3]'}`}>
                                            {req.met && <Check className="h-3 w-3 text-white" />}
                                        </div>
                                        <span className={`text-sm ${req.met ? 'text-[#7F9C8B]' : 'text-[#9AA5B1]'}`}>
                                            {req.text}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        
                        <Button
                            type="submit"
                            className="w-full senda-btn-primary"
                            disabled={loading || !allRequirementsMet}
                            data-testid="change-password-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Actualizando...
                                </>
                            ) : (
                                'Actualizar contraseña'
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
